// useDeviceFingerprint Hook
// Location: src/hooks/useDeviceFingerprint.ts
// Purpose: Generate unique device identifier for session tracking

import { useEffect, useState } from 'react';

export interface DeviceInfo {
  fingerprint: string;
  name: string;
  type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string;
  os: string;
  user_agent: string;
}

export function useDeviceFingerprint() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    const generateFingerprint = async () => {
      try {
        const userAgent = navigator.userAgent;
        const screenRes = `${screen.width}x${screen.height}x${screen.colorDepth}`;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const language = navigator.language;
        const platform = navigator.platform;
        const hardwareConcurrency = navigator.hardwareConcurrency || 0;
        
        // Create fingerprint string
        const rawString = `${userAgent}|${screenRes}|${timezone}|${language}|${platform}|${hardwareConcurrency}`;
        
        // Generate SHA-256 hash
        const encoder = new TextEncoder();
        const data = encoder.encode(rawString);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const fingerprint = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        // Detect device type
        const isMobile = /Mobi|Android|iPhone/i.test(userAgent);
        const isTablet = /Tablet|iPad/i.test(userAgent);
        let type: 'desktop' | 'mobile' | 'tablet' | 'unknown' = 'unknown';
        
        if (isTablet) {
          type = 'tablet';
        } else if (isMobile) {
          type = 'mobile';
        } else if (/Windows|Mac|Linux/i.test(userAgent)) {
          type = 'desktop';
        }
        
        // Detect browser
        let browser = 'Unknown';
        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
          browser = 'Chrome';
        } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
          browser = 'Safari';
        } else if (userAgent.includes('Firefox')) {
          browser = 'Firefox';
        } else if (userAgent.includes('Edg')) {
          browser = 'Edge';
        } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
          browser = 'Opera';
        }
        
        // Detect OS
        let os = 'Unknown';
        if (userAgent.includes('Windows NT 10.0')) {
          os = 'Windows 10';
        } else if (userAgent.includes('Windows NT 6.3')) {
          os = 'Windows 8.1';
        } else if (userAgent.includes('Windows NT 6.2')) {
          os = 'Windows 8';
        } else if (userAgent.includes('Windows')) {
          os = 'Windows';
        } else if (userAgent.includes('Mac OS X')) {
          const version = userAgent.match(/Mac OS X ([\d_]+)/);
          os = version ? `macOS ${version[1].replace(/_/g, '.')}` : 'macOS';
        } else if (userAgent.includes('Linux')) {
          os = 'Linux';
        } else if (userAgent.includes('Android')) {
          const version = userAgent.match(/Android ([\d.]+)/);
          os = version ? `Android ${version[1]}` : 'Android';
        } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
          const version = userAgent.match(/OS ([\d_]+)/);
          os = version ? `iOS ${version[1].replace(/_/g, '.')}` : 'iOS';
        }
        
        // Create device name
        const deviceName = `${browser} on ${os}`;
        
        setDeviceInfo({
          fingerprint,
          name: deviceName,
          type,
          browser,
          os,
          user_agent: userAgent
        });
      } catch (error) {
        console.error('Device fingerprint generation error:', error);
        
        // Fallback to basic fingerprint if crypto API fails
        const userAgent = navigator.userAgent;
        const fallbackString = `${userAgent}|${Date.now()}`;
        const fallbackFingerprint = btoa(fallbackString).substring(0, 64);
        
        setDeviceInfo({
          fingerprint: fallbackFingerprint,
          name: 'Unknown Device',
          type: 'unknown',
          browser: 'Unknown',
          os: 'Unknown',
          user_agent: userAgent
        });
      } finally {
        setIsGenerating(false);
      }
    };

    generateFingerprint();
  }, []);

  return {
    deviceInfo,
    isGenerating,
    isReady: deviceInfo !== null
  };
}